import { useEffect, useState } from "react";
import { ArrowRight, Newspaper } from "lucide-react";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";
import { SubpageShell } from "@/components/landing/SubpageShell";
import { CheckoutModal } from "@/components/landing/CheckoutModal";
import { Reveal } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndex() {
    const [posts, setPosts] = useState(null);
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    useEffect(() => {
        trackEvent("page_view");
        fetch(`${API}/blog`)
            .then((r) => (r.ok ? r.json() : []))
            .then(setPosts)
            .catch(() => setPosts([]));
    }, []);

    return (
        <SubpageShell onQuote={() => setCheckoutOpen(true)}>
            <Seo
                title="The AdvantaShield Blog — Extended Car Warranty, Explained"
                siteName="AdvantaShield"
                description="Plain-English articles on extended car warranties, vehicle service contracts, repair costs, and coverage — written by dealership service professionals."
                image="/og-image.png"
            />

            <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-sky-400">
                    The Blog
                </p>
                <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
                    Extended car warranty,{" "}
                    <span className="text-sky-400">explained.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                    Weekly plain-English articles on coverage, repair costs, and how
                    claims really work — written by the people behind the service
                    counter.
                </p>
            </Reveal>

            <div className="mt-14" data-testid="blog-list">
                {posts === null ? (
                    <p className="py-10 text-sm text-slate-500">Loading…</p>
                ) : posts.length === 0 ? (
                    <div className="rounded-3xl border border-white/10 bg-[#121722] p-10 text-center">
                        <Newspaper className="mx-auto h-8 w-8 text-sky-400/60" />
                        <p className="mt-4 text-sm text-slate-400">
                            Articles are on the way — check back soon.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {posts.map((p, i) => (
                            <Reveal key={p.id} delay={i * 0.06} className="h-full">
                                <a
                                    href={`/blog/${p.slug}`}
                                    data-testid={`blog-card-${p.slug}`}
                                    className="group flex h-full flex-col rounded-3xl border border-white/10 bg-[#121722] p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-sky-400/40"
                                >
                                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                        {new Date(p.created_at).toLocaleDateString(
                                            "en-US",
                                            {
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                            }
                                        )}
                                    </p>
                                    <h2 className="mt-3 font-display text-xl font-semibold leading-snug tracking-tight text-white transition-colors group-hover:text-sky-300">
                                        {p.title}
                                    </h2>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">
                                        {p.excerpt}
                                    </p>
                                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-sky-400">
                                        Read article
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </a>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>

            <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
        </SubpageShell>
    );
}
